import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ShieldCheck, Users, FileStack, IndianRupee, HardDrive, Sparkles,
  TrendingUp, Crown, Ban, CheckCircle, UserCog, RefreshCw, Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { api, formatBytes } from '../lib/api';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useSEO } from '../lib/seo';

export default function AdminPage() {
  useSEO({ title: 'Admin Console — ValutDocu', description: 'Internal admin dashboard.' });
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Unlocking the vault…</div>;
  if (!user || typeof user !== 'object') return <Navigate to="/login" state={{ from: '/admin' }} replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <div data-testid="admin-page" className="vault-grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 space-y-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold">Internal · Admin Only</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tighter mt-2">
              Vault <span className="gold-text">Control Room</span>
            </h1>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-vault-gold/10 border border-vault-gold/30 text-xs font-mono tracking-wider text-vault-gold">
            <Crown className="w-3.5 h-3.5" /> ADMIN
          </div>
        </div>

        <OverviewSection />
        <ToolAnalyticsSection />
        <UserManagementSection />
      </div>
    </div>
  );
}

function StatTile({ Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="tool-card rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-vault-gold" />
        <span className="text-xs tracking-[0.2em] uppercase font-mono text-gray-400">{label}</span>
      </div>
      <div className="text-3xl font-heading font-bold tracking-tighter text-white">{value}</div>
      {sub && <p className="text-xs text-gray-500 mt-1.5">{sub}</p>}
    </div>
  );
}

function OverviewSection() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const fetchOverview = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/overview');
      setData(data);
    } catch (e) {
      toast.error('Failed to load overview');
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchOverview(); }, [fetchOverview]);

  if (loading || !data) {
    return (
      <div data-testid="admin-overview-loading" className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="tool-card rounded-xl p-5 animate-pulse">
            <div className="h-3 w-20 bg-white/10 rounded mb-3" />
            <div className="h-8 w-16 bg-white/10 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <section data-testid="admin-overview">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading font-semibold">Overview</h2>
        <button data-testid="refresh-overview" onClick={fetchOverview} className="text-xs text-gray-400 hover:text-vault-gold inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile testId="stat-files-today" Icon={FileStack} label="Files today" value={data.files.today.toLocaleString()} sub={`${data.files.month.toLocaleString()} this month · ${data.files.total.toLocaleString()} total`} />
        <StatTile testId="stat-users" Icon={Users} label="Users" value={data.users.total.toLocaleString()} sub={`${data.users.pro} pro · +${data.users.today} today`} />
        <StatTile testId="stat-revenue" Icon={IndianRupee} label="Revenue (month)" value={`₹${data.revenue.month_inr.toLocaleString()}`} sub={`₹${data.revenue.total_inr.toLocaleString()} all-time · ${data.revenue.paid_count} orders`} />
        <StatTile testId="stat-ai" Icon={Sparkles} label="AI calls today" value={data.ai.calls_today.toLocaleString()} sub={`${data.ai.calls_total.toLocaleString()} all-time`} />
        <StatTile testId="stat-storage" Icon={HardDrive} label="Storage used" value={`${data.storage.mb} MB`} sub="Temp uploads in vault" />
        <StatTile testId="stat-new-users" Icon={TrendingUp} label="New signups" value={data.users.month.toString()} sub="This month" />
        <StatTile testId="stat-pro-rate" Icon={Crown} label="Pro conversion" value={`${data.users.total > 0 ? Math.round((data.users.pro / data.users.total) * 100) : 0}%`} sub={`${data.users.pro}/${data.users.total} users`} />
        <StatTile testId="stat-files-total" Icon={ShieldCheck} label="Total processed" value={data.files.total.toLocaleString()} sub="All time" />
      </div>
    </section>
  );
}

function ToolAnalyticsSection() {
  const [analytics, setAnalytics] = React.useState(null);
  const [days, setDays] = React.useState(30);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics/tools?days=${days}`).then(({ data }) => {
      setAnalytics(data);
    }).catch(() => toast.error('Failed to load analytics')).finally(() => setLoading(false));
  }, [days]);

  return (
    <section data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading font-semibold">Tool usage</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`days-${d}`}
              onClick={() => setDays(d)}
              className={`text-xs px-3 py-1.5 rounded-full ${days === d ? 'bg-vault-gold text-vault-paper' : 'bg-vault-paper/60 text-gray-300 border border-white/10'}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      {loading || !analytics ? (
        <div className="tool-card rounded-xl p-8 text-center text-gray-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="tool-card rounded-xl p-5">
            <h3 className="text-sm font-mono tracking-wider uppercase text-gray-400 mb-3">Top tools</h3>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={analytics.by_tool.slice(0, 10)} margin={{ top: 10, right: 10, bottom: 40, left: 0 }}>
                  <XAxis dataKey="tool" angle={-30} textAnchor="end" stroke="#9CA3AF" fontSize={10} interval={0} />
                  <YAxis stroke="#9CA3AF" fontSize={10} />
                  <Tooltip
                    contentStyle={{ background: '#0A1628', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 8, fontSize: 12 }}
                    cursor={{ fill: 'rgba(245,166,35,0.08)' }}
                  />
                  <Bar dataKey="count" fill="#F5A623" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="tool-card rounded-xl p-5">
            <h3 className="text-sm font-mono tracking-wider uppercase text-gray-400 mb-3">Daily volume</h3>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={analytics.daily} margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={10} />
                  <YAxis stroke="#9CA3AF" fontSize={10} />
                  <Tooltip contentStyle={{ background: '#0A1628', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#F5A623" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function UserManagementSection() {
  const [users, setUsers] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [q, setQ] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [busyId, setBusyId] = React.useState(null);

  const fetchUsers = React.useCallback(async () => {
    try {
      const { data } = await api.get('/admin/users', { params: { page, q, limit: 20 } });
      setUsers(data.users);
      setTotal(data.total);
    } catch { toast.error('Failed to load users'); }
  }, [page, q]);

  React.useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const updateUser = async (id, patch) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/users/${id}`, patch);
      toast.success('User updated');
      await fetchUsers();
    } catch (e) {
      toast.error('Failed to update user');
    } finally { setBusyId(null); }
  };

  return (
    <section data-testid="admin-users">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <h2 className="text-xl font-heading font-semibold">Users ({total.toLocaleString()})</h2>
        <Input
          data-testid="user-search"
          placeholder="Search by email or name…"
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          className="bg-vault-bg border-white/10 max-w-xs"
        />
      </div>
      <div className="tool-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-vault-bg/50 text-xs uppercase tracking-wider text-gray-400 border-b border-white/10">
              <tr>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Joined</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-3">
                    <div className="font-medium text-white">{u.name || '—'}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </td>
                  <td className="p-3 text-gray-300">
                    {u.role === 'admin' ? <span className="text-vault-gold">admin</span> : 'user'}
                  </td>
                  <td className="p-3">
                    {u.plan === 'pro' ? (
                      <span className="inline-flex items-center gap-1 text-vault-gold text-xs">
                        <Crown className="w-3 h-3" /> Pro
                      </span>
                    ) : <span className="text-gray-400 text-xs">Free</span>}
                  </td>
                  <td className="p-3">
                    {u.suspended ? <span className="text-red-400 text-xs">Suspended</span> : <span className="text-green-400 text-xs">Active</span>}
                  </td>
                  <td className="p-3 text-xs text-gray-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        data-testid={`toggle-plan-${u.id}`}
                        disabled={busyId === u.id || u.role === 'admin'}
                        onClick={() => updateUser(u.id, { plan: u.plan === 'pro' ? 'free' : 'pro' })}
                        className="text-xs text-gray-300 hover:text-vault-gold disabled:opacity-30"
                      >
                        {u.plan === 'pro' ? 'Revert to Free' : 'Make Pro'}
                      </button>
                      <span className="text-white/10">·</span>
                      <button
                        data-testid={`toggle-suspended-${u.id}`}
                        disabled={busyId === u.id || u.role === 'admin'}
                        onClick={() => updateUser(u.id, { suspended: !u.suspended })}
                        className="text-xs text-gray-300 hover:text-red-400 disabled:opacity-30"
                      >
                        {u.suspended ? <CheckCircle className="w-4 h-4 inline" /> : <Ban className="w-4 h-4 inline" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan="6" className="text-center py-8 text-gray-500">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {total > 20 && (
          <div className="flex justify-between items-center p-3 border-t border-white/10 text-xs text-gray-400">
            <span>Page {page} · {Math.ceil(total / 20)} pages</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded bg-vault-paper/60 disabled:opacity-30">Prev</button>
              <button disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded bg-vault-paper/60 disabled:opacity-30">Next</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
